import React, { FC, useState } from 'react'
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import {
  FormatInsertRule,
  SoapSection,
  soapSectionLabels,
} from '@/features/internal/format-insert-rules/types/formatInsertRule'
import { APP_CONFIG } from '@/shared/constants/config'
import { CustomButton, CancelButton } from '../../molecules/CustomButton'

type Props = {
  rule?: FormatInsertRule
  onSubmit: (rule: FormatInsertRule) => void
  onCancel: () => void
  isLoading?: boolean
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}))

const KeywordChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.light,
  '& .MuiChip-deleteIcon': {
    color: theme.palette.primary.dark,
  },
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
  },
  marginBottom: theme.spacing(2),
}))

export const FormatInsertRuleForm: FC<Props> = ({
  rule,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<Partial<FormatInsertRule>>({
    name: rule?.name || '',
    description: rule?.description || '',
    keywords: rule?.keywords || [],
    target_section: rule?.target_section || 'educational_plans',
    insert_text: rule?.insert_text || '',
    is_active: rule?.is_active ?? true,
  })

  const [keywordInput, setKeywordInput] = useState('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'ルール名は必須です'
    }

    if (!formData.keywords?.length) {
      newErrors.keywords = '最低1つのキーワードが必要です'
    }

    if (!formData.insert_text?.trim()) {
      newErrors.insert_text = '挿入テキストは必須です'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const addKeyword = () => {
    const trimmed = keywordInput.trim()
    if (trimmed && !formData.keywords?.includes(trimmed)) {
      setFormData({
        ...formData,
        keywords: [...(formData.keywords || []), trimmed],
      })
      setKeywordInput('')
      if (errors.keywords) {
        setErrors({ ...errors, keywords: '' })
      }
    }
  }

  const removeKeyword = (index: number) => {
    setFormData({
      ...formData,
      keywords: formData.keywords?.filter((_, i) => i !== index) || [],
    })
  }

  const createSubmitData = (): FormatInsertRule => {
    const {
      name,
      keywords,
      target_section,
      insert_text,
      is_active,
      description,
    } = formData

    if (
      !name?.trim() ||
      !keywords?.length ||
      !target_section ||
      !insert_text?.trim()
    ) {
      throw new Error('必須フィールドが不足しています')
    }

    return {
      name: name.trim(),
      keywords,
      target_section,
      insert_text: insert_text.trim(),
      is_active: is_active ?? true,
      description: description?.trim() || undefined,
      ...(rule?.id && { id: rule.id }),
      ...(rule?.created_at && { created_at: rule.created_at }),
      ...(rule?.updated_at && { updated_at: rule.updated_at }),
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      try {
        const submitData = createSubmitData()
        onSubmit(submitData)
      } catch (_error) {
        // フォームデータの作成に失敗した場合の処理
      }
    }
  }

  const handleKeywordInputKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addKeyword()
    }
  }

  return (
    <StyledPaper elevation={2}>
      <Typography variant="h6" gutterBottom>
        {rule ? 'ルール編集' : 'ルール作成'}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <StyledTextField
              fullWidth
              label="ルール名"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              error={!!errors.name}
              helperText={errors.name}
              placeholder="例：血圧薬服薬指導"
              required
              inputProps={{
                maxLength: APP_CONFIG.FORMAT_INSERT_RULE.MAX_NAME_LENGTH,
              }}
            />
          </Grid>

          <Grid size={12}>
            <StyledTextField
              fullWidth
              label="説明"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="ルールの詳細説明（任意）"
              multiline
              rows={2}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>
              キーワード{' '}
              <span style={{ color: colorTokens.special.changeNegative }}>
                *
              </span>
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                size="small"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={handleKeywordInputKeyPress}
                placeholder="キーワードを入力してEnterキーで追加"
                error={!!errors.keywords}
              />
              <CustomButton variant="outlined" onClick={addKeyword}>
                追加
              </CustomButton>
            </Box>
            {errors.keywords && (
              <Typography variant="caption" color="error">
                {errors.keywords}
              </Typography>
            )}
            <Box display="flex" flexWrap="wrap" mt={1}>
              {formData.keywords?.map((keyword, index) => (
                <KeywordChip
                  key={index}
                  label={keyword}
                  onDelete={() => removeKeyword(index)}
                  size="small"
                />
              ))}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>挿入先セクション</InputLabel>
              <Select
                value={formData.target_section}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    target_section: e.target.value as SoapSection,
                  })
                }
                label="挿入先セクション"
              >
                {Object.entries(soapSectionLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  color="primary"
                />
              }
              label="このルールを有効にする"
            />
          </Grid>

          <Grid size={12}>
            <StyledTextField
              fullWidth
              label="挿入テキスト"
              value={formData.insert_text}
              onChange={(e) =>
                setFormData({ ...formData, insert_text: e.target.value })
              }
              error={!!errors.insert_text}
              helperText={errors.insert_text}
              placeholder="キーワードが検出された際に挿入するテキスト"
              multiline
              rows={3}
              required
            />
          </Grid>

          <Grid size={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <CancelButton variant="outlined" onClick={onCancel}>
                キャンセル
              </CancelButton>
              <CustomButton
                type="submit"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? '処理中...' : rule ? '更新' : '作成'}
              </CustomButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </StyledPaper>
  )
}
